/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type JSX,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
// Добавили Navigate для автоматического редиректа
import {
  BrowserRouter,
  Route,
  useLocation,
  Routes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { TooltipProvider } from "@vector-im/compound-web";

// --- Убедитесь, что импорт из ClientContext выглядит именно так и он один на весь файл ---
import { ClientProvider } from "./ClientContext";

import { isGuestOrigin } from "./auth/access";
import { StaffGate } from "./auth/StaffGate";
import { GuestInviteGate } from "./auth/GuestInviteGate";
import { StaffSessionBridge } from "./auth/StaffSessionBridge";
import { StaffCallRedirect } from "./auth/StaffCallRedirect";
import { StaffJoinSetup } from "./auth/StaffJoinSetup";
import { JoinBridgeFrame } from "./auth/JoinBridgeFrame";
import { restoreJoinHandoff } from "./auth/joinSession";
import { HomePage } from "./home/HomePage";
// Импорты LoginPage и RegisterPage удалены за ненадобностью
import { useTranslation } from "react-i18next";
import { RoomAccessNotice } from "./room/RoomAccessNotice";
import { RoomAvailabilityGate } from "./room/RoomAvailabilityGate";
import { RoomPage } from "./room/RoomPage";
import { LoginPage } from "./auth/LoginPage";
// import { RegisterPage } from "./auth/RegisterPage"; // Убираем регистрацию
import { ErrorPage, LoadingPage } from "./FullScreenView";
import { Initializer } from "./initializer";
import { widget } from "./widget";
import { useTheme } from "./useTheme";
import { ProcessorProvider } from "./livekit/TrackProcessorContext";
import { type AppViewModel } from "./state/AppViewModel";
import { MediaDevicesContext } from "./MediaDevicesContext";
import { getUrlParams, HeaderStyle, useUrlParams } from "./UrlParams";
import { AppBar } from "./AppBar";

import { cleanAppearanceParams } from "./utils/cleanAppearanceParams";
import { RecordPage } from "./RecordPage";
import { StaffRecordingRedirect } from "./auth/StaffRecordingRedirect";

const SentryRoute = Sentry.withSentryReactRouterV7Routing(Route);

interface SimpleProviderProps {
  children: JSX.Element;
}

// Switching back into a call must remount the full client and its single-tab guard.
const PageClientProvider: FC<SimpleProviderProps> = ({ children }) => {
  const recordingOnly =
    useLocation().pathname.replace(/\/+$/, "") === "/record";
  return (
    <ClientProvider
      key={recordingOnly ? "record" : "live"}
      recordingOnly={recordingOnly}
    >
      {children}
    </ClientProvider>
  );
};

const BackgroundProvider: FC<SimpleProviderProps> = ({ children }) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  useEffect(() => {
    if (widget) return;
    const cleaned = cleanAppearanceParams(location);
    if (cleaned.search === location.search && cleaned.hash === location.hash)
      return;
    void navigate(
      {
        pathname,
        search: cleaned.search,
        hash: cleaned.hash,
      },
      { replace: true, state: location.state },
    );
  }, [location, pathname, navigate]);
  const { background } = useUrlParams();

  useEffect(() => {
    document
      .getElementsByTagName("body")[0]
      .setAttribute("data-background", background);
    const path = pathname.replace(/\/+$/, "") || "/";
    document.body.dataset.pageLayout = [
      "/",
      "/login",
      "/record",
      "/recordings",
      "/connect",
      "/auth/bridge",
    ].includes(path)
      ? "document"
      : "call";
  }, [pathname, background]);

  return children;
};

const ThemeProvider: FC<SimpleProviderProps> = ({ children }) => {
  useTheme();
  return children;
};

// --- ИСПРАВЛЕННЫЙ КОМПОНЕНТ ДЛЯ ЗАЩИТЫ МАРШРУТОВ ---
// Обязательно добавьте useClientLegacy в импорты сверху:
// import { ClientProvider, useClientLegacy } from "./ClientContext";

const ProtectedRoute = StaffGate;

const RecordRoute: FC = () => {
  const location = useLocation();
  if (!import.meta.env.VITE_GUEST_URL || isGuestOrigin(window.location.origin))
    return <RecordPage />;
  if (new URLSearchParams(location.search).has("share"))
    return <StaffRecordingRedirect />;
  return (
    <ProtectedRoute>
      <StaffRecordingRedirect />
    </ProtectedRoute>
  );
};

const RoomRoute: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  if (new URLSearchParams(location.search).get("unavailable") === "1")
    return (
      <RoomAccessNotice
        title={t("instance.ui.room_not_found")}
        description={t("instance.ui.room_link_expired_hint")}
      />
    );
  if (widget)
    return (
      <RoomAvailabilityGate>
        <RoomPage />
      </RoomAvailabilityGate>
    );
  if (isGuestOrigin(window.location.origin)) {
    const expectedUserId = new URLSearchParams(location.search).get(
      "staffUser",
    );
    return expectedUserId ? (
      <RoomAvailabilityGate>
        <StaffGate expectedUserId={expectedUserId}>
          <RoomPage />
        </StaffGate>
      </RoomAvailabilityGate>
    ) : (
      <GuestInviteGate>
        <RoomAvailabilityGate>
          <RoomPage />
        </RoomAvailabilityGate>
      </GuestInviteGate>
    );
  }
  return (
    <ProtectedRoute>
      <StaffCallRedirect />
    </ProtectedRoute>
  );
};
const HomeRoute: FC = () =>
  isGuestOrigin(window.location.origin) ? (
    <HomePage />
  ) : (
    <ProtectedRoute>
      <HomePage />
    </ProtectedRoute>
  );

interface Props {
  vm: AppViewModel;
}
let appStartup: Promise<void> | undefined;
async function initializeApplication(): Promise<void> {
  return (appStartup ??= (async () => {
    await Initializer.init();
    if (!widget) await restoreJoinHandoff();
    await widget?.api.sendContentLoaded();
  })());
}

export const App: FC<Props> = ({ vm }) => {
  const [loaded, setLoaded] = useState(false);
  const [startupError, setStartupError] = useState<Error>();
  useEffect(() => {
    let cancelled = false;
    void initializeApplication()
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      .catch((cause) => {
        if (!cancelled)
          setStartupError(
            cause instanceof Error
              ? cause
              : new Error("Application initialization failed"),
          );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Since we are outside the router component, we cannot use useUrlParams here
  const { header } = useMemo(getUrlParams, []);
  if (loaded && window.location.pathname === "/auth/staff-bridge")
    return <StaffSessionBridge />;
  if (loaded && window.location.pathname === "/auth/bridge")
    return <JoinBridgeFrame />;

  const content = startupError ? (
    <ErrorPage widget={widget} error={startupError} />
  ) : loaded ? (
    <PageClientProvider>
      <MediaDevicesContext value={vm.mediaDevices}>
        <ProcessorProvider>
          <Sentry.ErrorBoundary
            fallback={(error) => <ErrorPage error={error} widget={widget} />}
          >
            <Routes>
              {/* --- ОТКРЫТЫЕ МАРШРУТЫ --- */}
              <SentryRoute
                path="/login"
                element={
                  isGuestOrigin(window.location.origin) ? (
                    <Navigate to="/" replace />
                  ) : (
                    <LoginPage />
                  )
                }
              />
              <SentryRoute
                path="/register"
                element={
                  <Navigate
                    to={isGuestOrigin(window.location.origin) ? "/" : "/login"}
                    replace
                  />
                }
              />

              {/* --- ЗАЩИЩЕННЫЕ МАРШРУТЫ --- */}
              <SentryRoute path="/" element={<HomeRoute />} />
              <SentryRoute
                path="/connect"
                element={
                  isGuestOrigin(window.location.origin) ? (
                    <Navigate to="/" replace />
                  ) : (
                    <ProtectedRoute>
                      <StaffJoinSetup />
                    </ProtectedRoute>
                  )
                }
              />

              <Route path="/record" element={<RecordRoute />} />

              <Route path="/recordings" element={<Navigate to="/" replace />} />

              {/* Звездочка (комната) ВСЕГДА должна быть в самом конце! */}
              {/* Комнаты тоже защищаем. Гости сюда не пройдут. */}
              <SentryRoute path="*" element={<RoomRoute />} />
            </Routes>
          </Sentry.ErrorBoundary>
        </ProcessorProvider>
      </MediaDevicesContext>
    </PageClientProvider>
  ) : (
    <LoadingPage />
  );

  return (
    <BrowserRouter>
      <BackgroundProvider>
        <ThemeProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              {header === HeaderStyle.AppBar ? (
                <AppBar>{content}</AppBar>
              ) : (
                content
              )}
            </Suspense>
          </TooltipProvider>
        </ThemeProvider>
      </BackgroundProvider>
    </BrowserRouter>
  );
};
